import {
  toRussianLocaleDateFromIso,
  toRussianLocaleDateTimeTzFromIso,
} from '@packages/toolbox/dist/utils/datetime';
import { MaybeRefOrGetter, toValue } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import {
  Computable,
  QuasarTableColumn,
  QuasarTableExportColumn,
} from 'SharedDir/declarations/shared';

export function DateColumn<T extends object>(
  column: QuasarTableColumn<T>,
  options: Partial<{ ifEmpty: unknown }> = {}
): QuasarTableColumn<T> {
  return {
    ...column,
    align: 'left',
    style: 'width: 110px;max-width: 180px;',
    format: (isoDate) => {
      if (!isoDate) {
        return options.ifEmpty ?? null;
      }
      return toRussianLocaleDateFromIso(isoDate);
    },
  };
}

export function DateTimeColumn<T extends object>(
  column: QuasarTableColumn<T>,
  options: Partial<{ ifEmpty: unknown }> = {}
): QuasarTableColumn<T> {
  const o = {
    ifEmpty: null,
    ...options,
  };
  return {
    ...column,
    style: 'word-break: break-all; text-wrap: nowrap;',
    format: (value) => {
      if (!value) {
        return options.ifEmpty;
      }
      return toRussianLocaleDateTimeTzFromIso(value);
    },
  };
}

export function BooleanColumn<T extends object>(
  column: QuasarTableColumn<T>,
  options: Partial<{ true: string; false: string; null: string }> = {}
): QuasarTableColumn<T> {
  return {
    ...column,
    align: 'center',
    format: (value) => {
      const defaultOptions = {
        true: 'Да',
        false: 'Нет',
        null: '-',
      };
      return { ...defaultOptions, ...options }[value];
    },
  };
}

export function DictionaryColumn<T extends object, C extends QuasarTableColumn<T>>(
  column: C,
  options: {
    dictionary: MaybeRefOrGetter<Record<string | number, unknown>>;
    defaultValue?: string;
  }
): QuasarTableColumn<T> {
  const { defaultValue = '' } = options;
  return {
    align: 'left',
    ...column,
    format: (value) => {
      if (value == null || typeof value === 'undefined') {
        return defaultValue;
      }
      return toValue(options.dictionary)[value] ?? defaultValue;
    },
  };
}

/**
 * Столбец таблицы, предназначенный для отображения денег
 * @param column
 * @param options
 * @constructor
 */
export function CurrencyColumn<T extends object, C extends QuasarTableColumn<T>>(
  column: C,
  options: {
    name?: string;
    numberOptions?: Intl.NumberFormatOptions;
  } = {}
): QuasarTableColumn<T> {
  const { name = '₽', numberOptions = { maximumFractionDigits: 2 } } = options;
  return {
    align: 'right',
    ...column,
    format: (value) => {
      if (value == null || typeof value === 'undefined') {
        return null;
      }
      const format = Intl.NumberFormat('ru-RU', numberOptions).format(value);
      return `${format} ${name}`;
    },
  };
}

export class ColumnLink<T extends object> implements QuasarTableColumn<T> {
  options: EntityLinkOptions<T>;

  label: string;

  name: string;

  field?: keyof T | ((val: T) => unknown);

  format?: (val, row: number) => unknown;

  align?: string;

  required?: boolean;

  sortable?: boolean;

  style?: string;

  classes?: string | ((val: T) => string);

  headerStyle?: string;

  headerClasses?: string;

  sort?: (a, b, rowA: T, rowB: T) => number;

  export?: QuasarTableExportColumn | false;

  hiddenDefault?: boolean;

  constructor(column: QuasarTableColumn<T>, options: EntityLinkOptions<T>) {
    Object.keys(column).forEach((key) => {
      this[key] = column[key];
    });

    this.label = column.label;
    this.name = column.name;
    this.field = column.field;

    this.options = options;
  }
}

export interface EntityLinkOptions<T extends object> {
  /**
   * Текст отображаемый в качестве ссылки
   */
  label?: Computable<string | number, T>;
  /**
   * Является ли ссылка активной в данный момент,
   * при не активности будет напечатан просто текст вместо гиперссылки.
   */
  active?: Computable<boolean, T>;
  /**
   * Является ли сущность на которую идет ссылка "удаленной"
   * под удаленной может пониматься широкий спектр состояний,
   * необязательно именно (*deletedAt*).
   * Текст "удаленной" сущности будет обернут в тэг `<s></s>`
   */
  deleted?: Computable<boolean, T>;
  location?: Computable<string | RouteLocationRaw | undefined, T>;
}

export function EntityColumn<T extends object>(
  column: QuasarTableColumn<T>,
  options: EntityLinkOptions<T>
) {
  return new ColumnLink(column, options);
}

export function IsEntityLinkColumn<T extends object>(
  column: QuasarTableColumn<T> | ColumnLink<T>
): column is ColumnLink<T> {
  return column instanceof ColumnLink;
}
